import { z } from "zod";
import type { TimeEntryType } from "@/lib/enums";
import type { MonthTotals } from "@/lib/month/metrics";
import { isMonthKey, type MonthKey } from "@/lib/month/month";

/**
 * Home and Guidance wire contract. Queries are Zod (strict: no personId can be passed; the
 * person comes from the session). Responses are plain interfaces the server builds.
 */

const monthKeySchema = z.string().refine(isMonthKey, "Use a month like 2026-10");

const MAX_SPAN = 12;

export const summaryQuerySchema = z.strictObject({
  month: monthKeySchema,
  span: z.coerce.number().int().min(1).max(MAX_SPAN).default(1),
});

export const monthQuerySchema = z.strictObject({ month: monthKeySchema });

export const kpiQuerySchema = z.strictObject({
  year: z.coerce.number().int().min(2000).max(2100),
});

/** Router search for Home and Guidance; a missing or invalid month means the current one. */
export const monthSearchSchema = z.object({
  month: monthKeySchema.optional().catch(undefined),
});

export interface MonthSummary extends MonthTotals {
  periodId: string;
  month: MonthKey;
  targetUtilization: number | null;
}

export interface MonthSummaryView {
  /** Oldest first. */
  months: MonthSummary[];
  /** The person's first and last PersonPeriod month: the switcher's range. */
  firstMonth: MonthKey | null;
  lastMonth: MonthKey | null;
}

export interface DemandMonthRow {
  demandPersonId: string;
  demandId: string;
  demandName: string;
  accountName: string | null;
  role: string | null;
  /** DemandPlan.GuidanceQuantity; null when the demand has hours but no plan. */
  planned: number | null;
  /** Productive hours on this assignment in the month. */
  logged: number;
  billable: number;
}

export interface MonthTaskRow {
  key: string;
  projectTaskId: string | null;
  taskName: string;
  projectName: string | null;
  type: TimeEntryType;
  hours: number;
}

export interface KpiCard {
  id: string;
  metric: string;
  orgLevel: string | null;
  target: number | null;
  actualDisplay: string | null;
  projectedDisplay: string | null;
}
